/**
 * HTML link formatting for SSR and the client. DOMPurify stays in `sanitize-html.client.ts`
 * so Amplify/Lambda never loads isomorphic-dompurify → jsdom.
 */

import { processLink } from '@/_utils/link.utils'

/**
 * Removes protocol prefixes (http://, https://) from link text while preserving href attributes
 * This improves readability by showing clean URLs in link text
 *
 * @param htmlContent - The HTML string containing links to process
 * @returns HTML string with protocol prefixes removed from link text only
 *
 * @example
 * // Input: <a href="https://example.com">https://example.com</a>
 * // Output: <a href="https://example.com">example.com</a>
 */
export const removeProtocolFromLinkText = (htmlContent: string): string => {
	if (!htmlContent) return ''

	// Match <a> tags and remove protocol from text content only, not href
	return htmlContent.replace(
		/(<a\b[^>]*>)([^<]+)(<\/a>)/gi,
		(fullMatch, openingTag, linkText, closingTag) => {
			const trimmedText = linkText.trim()

			// Only process if text starts with protocol
			if (!/^https?:\/\//i.test(trimmedText)) return fullMatch

			const textWithoutProtocol = trimmedText.replace(/^https?:\/\//i, '')
			return `${openingTag}${textWithoutProtocol}${closingTag}`
		}
	)
}

const urlPattern = /(https?:\/\/[^\s<>"']+)/gi

function convertUrlsInTextSegment(text: string): string {
	return text.replace(urlPattern, url => {
		const cleanUrl = url.trim()
		const processedLink = processLink(cleanUrl)
		const displayText = cleanUrl.replace(/^https?:\/\//, '')
		const attributes = [
			`href="${processedLink.href}"`,
			`title="${displayText}"`,
			processedLink.target ? `target="${processedLink.target}"` : '',
			processedLink.rel ? `rel="${processedLink.rel}"` : 'rel="noopener"',
			'class="formatted-link underline"'
		]
			.filter(Boolean)
			.join(' ')
		return `<a ${attributes}>${displayText}</a>`
	})
}

const ANCHOR_PLACEHOLDER_PREFIX = '\uFFFFANCHOR'
const ANCHOR_PLACEHOLDER_SUFFIX = '\uFFFF'

/**
 * Finds the end index (after closing '>') of the </a> that matches the opening <a> at startIndex.
 * Handles nested <a> tags. startIndex should be the index right after the opening <a...> '>'.
 */
function findMatchingClosingAnchor(html: string, startIndex: number): number {
	let depth = 1
	let i = startIndex
	while (i < html.length) {
		const openAngle = html.indexOf('<', i)
		if (openAngle === -1) return -1
		const afterOpen = openAngle + 1
		if (
			html.slice(afterOpen, afterOpen + 2).toLowerCase() === '/a' &&
			/[\s>]/.test(html[afterOpen + 2] ?? '')
		) {
			const closeAngle = html.indexOf('>', afterOpen + 2)
			if (closeAngle === -1) return -1
			depth--
			if (depth === 0) return closeAngle + 1
			i = closeAngle + 1
			continue
		}
		if (
			html.slice(afterOpen, afterOpen + 1).toLowerCase() === 'a' &&
			/[\s>]/.test(html[afterOpen + 1] ?? '')
		) {
			depth++
			const closeAngle = html.indexOf('>', afterOpen + 1)
			if (closeAngle === -1) return -1
			i = closeAngle + 1
			continue
		}
		i = openAngle + 1
	}
	return -1
}

/**
 * Replaces each <a>...</a> (including nested tags) with a placeholder; returns new string and list of originals.
 * Advances past each found anchor so nested/overlapping anchors are handled (outer processed first).
 */
function maskAnchorContents(htmlContent: string): { masked: string; anchors: string[] } {
	const anchors: string[] = []
	let masked = htmlContent
	let placeholderIndex = 0
	const openTagRe = /<a\b[^>]*>/gi
	let match: RegExpExecArray | null
	while ((match = openTagRe.exec(htmlContent)) !== null) {
		const openEnd = match.index + match[0].length
		const closeEnd = findMatchingClosingAnchor(htmlContent, openEnd)
		if (closeEnd === -1) continue
		const anchorHtml = htmlContent.slice(match.index, closeEnd)
		anchors.push(anchorHtml)
		const placeholder = `${ANCHOR_PLACEHOLDER_PREFIX}${placeholderIndex}${ANCHOR_PLACEHOLDER_SUFFIX}`
		placeholderIndex++
		masked = masked.replace(anchorHtml, placeholder)
		openTagRe.lastIndex = closeEnd
	}
	return { masked, anchors }
}

/**
 * Converts standalone URLs (https:// or http://) into clickable links.
 * Processes plain text and text nodes only; skips URLs inside tag attributes
 * and skips all text inside <a> tags (including after nested tags) to avoid nested anchors.
 *
 * @param htmlContent - The HTML string or plain text that may contain standalone URLs
 * @returns HTML string with standalone URLs in text converted to clickable links
 *
 * @example
 * // Input: "Visit https://medante.sk for more info"
 * // Output: "Visit <a href="https://medante.sk" ...>medante.sk</a> for more info"
 */
export const convertStandaloneUrlsToLinks = (htmlContent: string): string => {
	if (!htmlContent) return ''

	const { masked, anchors } = maskAnchorContents(htmlContent)

	// 1) Leading text (or whole string when no <): plain text before first tag
	// 2) Text nodes after > (no need for <a> lookbehind — anchors are masked)
	const withLinks = masked.replace(
		/^([^<]*?)(?=<|$)|>([^<]*?)(?=<|$)/g,
		(m, leading, afterAngle) => {
			const segment = leading !== undefined ? leading : afterAngle
			const processed = convertUrlsInTextSegment(segment)
			return leading !== undefined ? processed : '>' + processed
		}
	)

	let result = withLinks
	for (let i = 0; i < anchors.length; i++) {
		result = result.replace(
			`${ANCHOR_PLACEHOLDER_PREFIX}${i}${ANCHOR_PLACEHOLDER_SUFFIX}`,
			anchors[i]
		)
	}
	return result
}

const PHONE_MIN_DIGITS = 8
const PHONE_MAX_DIGITS = 15

/** Display run starting with `+` (international); allows spaces, parentheses, hyphens. */
const PHONE_DISPLAY_RE = /\+[\d\s().-]{7,}\d/g

function countDigits(s: string): number {
	return (s.match(/\d/g) ?? []).length
}

function isLikelyInternationalPhone(display: string): boolean {
	const n = countDigits(display)
	return n >= PHONE_MIN_DIGITS && n <= PHONE_MAX_DIGITS
}

function toTelHref(display: string): string {
	const compact = display.replace(/[\s().-]/g, '')
	if (/^\+/.test(compact)) return `tel:${compact}`
	if (/^00/.test(compact)) return `tel:+${compact.slice(2)}`
	if (/^\d+$/.test(compact)) return `tel:+${compact}`
	return `tel:${compact}`
}

function escapeHtmlText(s: string): string {
	return s
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
}

function telLinkHtml(display: string): string {
	const href = escapeHtmlText(toTelHref(display))
	const label = escapeHtmlText(display.trim())
	return `<a href="${href}" rel="nofollow noopener noreferrer">${label}</a>`
}

function convertPhonesInTextSegment(segment: string): string {
	return segment.replace(PHONE_DISPLAY_RE, match => {
		if (!isLikelyInternationalPhone(match)) return match
		return telLinkHtml(match)
	})
}

/**
 * Wraps international phone numbers in `tel:` links. Runs on HTML with existing `<a>` tags
 * masked so mailto/https links are untouched. Text between any tags (including inside
 * `<strong>` / `<b>`) is processed, so e.g. `<strong>+421 …</strong>` becomes
 * `<strong><a href="tel:…">+421 …</a></strong>` and stays bold.
 */
function convertPhoneNumbersToTelLinksSinglePass(htmlContent: string): string {
	const { masked, anchors } = maskAnchorContents(htmlContent)

	const withPhones = masked.replace(
		/^([^<]*?)(?=<|$)|>([^<]*?)(?=<|$)/g,
		(_m, leading: string | undefined, afterAngle: string | undefined) => {
			const segment = (leading !== undefined ? leading : afterAngle) ?? ''
			const processed = convertPhonesInTextSegment(segment)
			return leading !== undefined ? processed : '>' + processed
		}
	)

	let result = withPhones
	for (let i = 0; i < anchors.length; i++) {
		result = result.replace(
			`${ANCHOR_PLACEHOLDER_PREFIX}${i}${ANCHOR_PLACEHOLDER_SUFFIX}`,
			anchors[i]
		)
	}
	return result
}

export const convertPhoneNumbersToTelLinks = (htmlContent: string): string => {
	if (!htmlContent) return ''
	let out = convertPhoneNumbersToTelLinksSinglePass(htmlContent)
	// Second pass: display text inside new `tel:` links would otherwise match `PHONE_DISPLAY_RE` again.
	out = convertPhoneNumbersToTelLinksSinglePass(out)
	return out
}

/**
 * Formats order-flow product banner HTML (title/body): `tel:` links, standalone URLs,
 * protocol cleanup. XSS filtering runs in the browser via `sanitizeHtml`.
 */
export const sanitizeOrderProductBannerHtml = (htmlContent: string): string => {
	if (!htmlContent) return ''

	const normalizedHtml = unescapeHtmlQuotes(htmlContent)
	const withPhones = convertPhoneNumbersToTelLinks(normalizedHtml)
	const withStandaloneLinks = convertStandaloneUrlsToLinks(withPhones)
	return removeProtocolFromLinkText(withStandaloneLinks)
}

/**
 * Converts text patterns like "https://medante.sk[Medante]" or
 * "mailto:recepcia@medante.sk[recepcia@medante.sk]" into proper HTML links
 * with SEO-optimized attributes for better accessibility and search engine optimization
 *
 * @param htmlContent - The HTML string that may contain link patterns to convert
 * @returns HTML string with patterns converted to proper <a> tags with SEO attributes
 *
 * @example
 * // Input: "Visit https://medante.sk[Medante] for more info"
 * // Output: "Visit <a href="https://medante.sk" title="Medante" rel="noopener" target="_blank">Medante</a> for more info"
 * // Input: "Write mailto:recepcia@medante.sk[recepcia@medante.sk]"
 * // Output: "Write <a href="mailto:recepcia@medante.sk" title="recepcia@medante.sk" ...>recepcia@medante.sk</a>"
 */
export const formatSpecialLinkPatterns = (htmlContent: string): string => {
	if (!htmlContent) return ''

	// Pattern: https://domain.com[Link Text], http://..., or mailto:user@domain[Link Text]
	const linkPattern = /((?:https?:\/\/|mailto:)[^\s\[\]]+)\[([^\]]+)\]/gi

	return htmlContent.replace(linkPattern, (fullMatch, url, linkText) => {
		// Clean and validate URL
		const cleanUrl = url.trim()
		const cleanLinkText = linkText.trim()

		if (!cleanUrl || !cleanLinkText) return fullMatch

		const processedLink = processLink(cleanUrl)

		// Build SEO-optimized attributes
		const attributes = [
			`href="${processedLink.href}"`,
			`title="${cleanLinkText}"`,
			processedLink.target ? `target="${processedLink.target}"` : '',
			processedLink.rel ? `rel="${processedLink.rel}"` : 'rel="noopener"',
			'class="formatted-link underline"'
		]
			.filter(Boolean)
			.join(' ')

		return `<a ${attributes}>${cleanLinkText}</a>`
	})
}

/**
 * Unescapes JSON-style escaped quotes in HTML (e.g. from CMS/API).
 * Converts \" to " so that attributes like href=\"...\" parse correctly.
 *
 * @param htmlContent - HTML string that may contain backslash-escaped quotes
 * @returns HTML string with escaped quotes restored
 */
export const unescapeHtmlQuotes = (htmlContent: string): string => {
	if (!htmlContent) return ''
	return htmlContent.replace(/\\"/g, '"')
}

/**
 * Link formatting for EditorJS / FAQ HTML. `sanitize` is kept for call-site compatibility;
 * XSS filtering is browser-only (`sanitizeHtml`) so serverless SSR never loads jsdom.
 *
 * @param htmlContent - Raw HTML content to process
 * @returns HTML with special link patterns, standalone URLs, and protocol cleanup
 */
export const sanitizeAndProcessHtml = (htmlContent: string, _sanitize = true): string => {
	if (!htmlContent) return ''

	const normalizedHtml = unescapeHtmlQuotes(htmlContent)
	const withSpecialLinks = formatSpecialLinkPatterns(normalizedHtml)
	const withStandaloneLinks = convertStandaloneUrlsToLinks(withSpecialLinks)
	return removeProtocolFromLinkText(withStandaloneLinks)
}

const VOID_HTML_TAGS_FOR_TRUNCATE = new Set([
	'area',
	'base',
	'br',
	'col',
	'embed',
	'hr',
	'img',
	'input',
	'link',
	'meta',
	'param',
	'source',
	'track',
	'wbr'
])

function countTextCharsOutsideTags(html: string): number {
	let count = 0
	let i = 0
	while (i < html.length) {
		if (html[i] === '<') {
			const end = html.indexOf('>', i)
			if (end === -1) break
			i = end + 1
			continue
		}
		count++
		i++
	}
	return count
}

function parseHtmlTagFragment(tagInner: string): { name: string; closing: boolean } | null {
	const trimmed = tagInner.trim()
	if (!trimmed || trimmed[0] === '!' || trimmed[0] === '?') return null
	if (trimmed[0] === '/') {
		const nameMatch = trimmed.slice(1).match(/^([a-zA-Z0-9:-]+)/)
		return nameMatch ? { name: nameMatch[1].toLowerCase(), closing: true } : null
	}
	const nameMatch = trimmed.match(/^([a-zA-Z0-9:-]+)/)
	return nameMatch ? { name: nameMatch[1].toLowerCase(), closing: false } : null
}

/**
 * Truncates HTML to a maximum number of visible text characters while keeping tags balanced.
 */
export function truncateHtmlPreservingTags(
	html: string,
	maxChars: number
): { html: string; wasTruncated: boolean } {
	if (!html) {
		return { html: '', wasTruncated: false }
	}
	if (maxChars <= 0) {
		return { html: '', wasTruncated: countTextCharsOutsideTags(html) > 0 }
	}

	if (countTextCharsOutsideTags(html) <= maxChars) {
		return { html, wasTruncated: false }
	}

	let i = 0
	let textChars = 0
	let out = ''
	const tagStack: string[] = []

	while (i < html.length) {
		if (html[i] === '<') {
			const end = html.indexOf('>', i)
			if (end === -1) {
				out += html.slice(i)
				break
			}
			const inner = html.slice(i + 1, end)
			const fullTag = html.slice(i, end + 1)
			const parsed = parseHtmlTagFragment(inner)
			i = end + 1

			if (!parsed) {
				out += fullTag
				continue
			}

			const { name, closing } = parsed
			if (closing) {
				while (tagStack.length > 0 && tagStack[tagStack.length - 1] !== name) {
					tagStack.pop()
				}
				if (tagStack.length > 0 && tagStack[tagStack.length - 1] === name) {
					tagStack.pop()
				}
				out += fullTag
				continue
			}

			const selfClosing = fullTag.endsWith('/>') || VOID_HTML_TAGS_FOR_TRUNCATE.has(name)
			out += fullTag
			if (!selfClosing) {
				tagStack.push(name)
			}
			continue
		}

		const nextTag = html.indexOf('<', i)
		const endOfText = nextTag === -1 ? html.length : nextTag
		const textChunk = html.slice(i, endOfText)
		const remaining = maxChars - textChars

		if (textChunk.length <= remaining) {
			out += textChunk
			textChars += textChunk.length
			i = endOfText
			continue
		}

		out += textChunk.slice(0, remaining)
		while (tagStack.length > 0) {
			out += `</${tagStack.pop()}>`
		}
		return { html: out, wasTruncated: true }
	}

	while (tagStack.length > 0) {
		out += `</${tagStack.pop()}>`
	}
	return { html: out, wasTruncated: true }
}
